import { useEffect } from 'react';
import { useParams } from 'react-router';
import { Flex, Spin } from 'antd';
import { CheckCircleOutlined, CloseCircleOutlined, ExclamationCircleOutlined } from '@ant-design/icons';
import { useSupport } from '@/hooks/use-support/use-support';
import styles from './verify-support-ticket.module.scss';
import { AxiosError } from 'axios';
import { useTranslation } from 'react-i18next';

export function VerifySupportTicket() {
  const { t } = useTranslation('support');
  const { token } = useParams<{ token: string }>();
  const { verifyTicket, isVerifying, isVerified, isVerificationError, verificationError } = useSupport();

  useEffect(() => {
    if (token) {
      verifyTicket(token);
    }
  }, [token, verifyTicket]);

  const getStatusContent = () => {
    if (isVerifying) {
      return (
        <Flex vertical align="center" gap={24} className={styles.container}>
          <Spin size="large" />
          <h2 className={styles.title}>{t('verify.verifying')}</h2>
        </Flex>
      );
    }

    if (isVerified) {
      return (
        <Flex vertical align="center" gap={24} className={styles.container}>
          <CheckCircleOutlined className={styles.successIcon} />
          <h2 className={styles.title}>{t('verify.verified.title')}</h2>
          <p className={styles.message}>{t('verify.verified.message')}</p>
        </Flex>
      );
    }

    if (isVerificationError && verificationError) {
      const error = verificationError as AxiosError;
      const status = error.response?.status;

      if (status === 422) {
        return (
          <Flex vertical align="center" gap={24} className={styles.container}>
            <ExclamationCircleOutlined className={styles.warningIcon} />
            <h2 className={styles.title}>{t('verify.expired.title')}</h2>
            <p className={styles.message}>{t('verify.expired.message')}</p>
            <p className={styles.subMessage}>{t('verify.expired.hint')}</p>
          </Flex>
        );
      }

      if (status === 404) {
        return (
          <Flex vertical align="center" gap={24} className={styles.container}>
            <CloseCircleOutlined className={styles.errorIcon} />
            <h2 className={styles.title}>{t('verify.invalid.title')}</h2>
            <p className={styles.message}>{t('verify.invalid.message')}</p>
          </Flex>
        );
      }

      return (
        <Flex vertical align="center" gap={24} className={styles.container}>
          <CloseCircleOutlined className={styles.errorIcon} />
          <h2 className={styles.title}>{t('verify.failed.title')}</h2>
          <p className={styles.message}>{t('verify.failed.message')}</p>
        </Flex>
      );
    }

    return null;
  };

  return (
    <Flex justify="center" align="center" className={styles.wrapper}>
      {getStatusContent()}
    </Flex>
  );
}
